import React, { useState, useEffect, useRef } from "react";
import {
  Layout,
  Menu,
  Typography,
  Button,
  Row,
  Col,
  Space,
  Spin,
  message,
  ConfigProvider,
  Tag,
  Badge,
} from "antd";
import {
  CodeOutlined,
  CheckCircleFilled,
  PlayCircleFilled,
  RocketOutlined,
  BulbOutlined,
  ThunderboltFilled,
  CoffeeOutlined,
  DatabaseOutlined,
  ControlOutlined,
  FunctionOutlined,
  ApiOutlined,
  EditOutlined,
} from "@ant-design/icons";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import "./App.css";

import { Module, fetchCourseModules, LessonIcon } from "./courseData";

const { Header, Content } = Layout;
const { Title, Text } = Typography;

const iconMap: Record<LessonIcon, React.ReactNode> = {
  CodeOutlined: <CodeOutlined />,
  DatabaseOutlined: <DatabaseOutlined />,
  ControlOutlined: <ControlOutlined />,
  FunctionOutlined: <FunctionOutlined />,
  ApiOutlined: <ApiOutlined />,
  EditOutlined: <EditOutlined />,
  RocketOutlined: <RocketOutlined />,
};

const App: React.FC = () => {
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeLessonId, setActiveLessonId] = useState<string>("l1");
  const [code, setCode] = useState<string>("");
  const [output, setOutput] = useState<string>("");

  const [pyodide, setPyodide] = useState<any>(null);
  const [isExecuting, setIsExecuting] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [showSolution, setShowSolution] = useState<boolean>(false);

  const outputBuffer = useRef<string>("");

  useEffect(() => {
    const initializeApp = async () => {
      try {
        const data = await fetchCourseModules();
        setModules(data);
        if (data.length > 0 && data[0].lessons.length > 0) {
          setActiveLessonId(data[0].lessons[0].id);
        }

        const script = document.createElement("script");
        script.src = "https://cdn.jsdelivr.net/pyodide/v0.25.0/full/pyodide.js";
        script.async = true;

        script.onload = async () => {
          // @ts-ignore
          const pyodideInstance = await window.loadPyodide({
            indexURL: "https://cdn.jsdelivr.net/pyodide/v0.25.0/full/",
            stdout: (text: string) => {
              outputBuffer.current += text + "\n";
            },
            stderr: (text: string) => {
              outputBuffer.current += text + "\n";
            },
          });

          setPyodide(pyodideInstance);
          setLoading(false);
        };

        document.body.appendChild(script);
      } catch (error) {
        message.error("Failed to initialize the learning environment.");
        setLoading(false);
      }
    };

    initializeApp();
  }, []);

  const activeLesson = modules
    .flatMap((m) => m.lessons)
    .find((l) => l.id === activeLessonId);

  useEffect(() => {
    if (activeLesson) {
      setCode(activeLesson.starterCode || "");
      setOutput("");
      setIsError(false);
      setShowSolution(false);
    }
  }, [activeLesson]);

  const handleRunCode = async () => {
    if (!activeLesson || !pyodide) return;

    setIsExecuting(true);
    setOutput("Initializing neural execution engine...\n");
    setIsError(false);
    outputBuffer.current = "";

    try {
      await pyodide.runPythonAsync(code);
      const runOutput = outputBuffer.current;
      setOutput(runOutput || "Code executed successfully (No output printed).");

      if (activeLesson.solution) {
        const normalize = (str: string) => str.replace(/\s+/g, "");
        if (normalize(code) === normalize(activeLesson.solution)) {
          message.success({
            content: (
              <span>
                Flawless Execution! You nailed it! <RocketOutlined />
              </span>
            ),
            style: { marginTop: "10vh", fontSize: "16px", fontWeight: "bold" },
          });
        } else {
          message.warning({
            content:
              "It ran without errors, but might not be the exact solution. Keep tweaking!",
            style: { marginTop: "10vh" },
          });
        }
      }
    } catch (error: any) {
      setIsError(true);
      setOutput(outputBuffer.current + "\n" + error.message);
      message.error({
        content: "System Exception! Check the terminal for details.",
        style: { marginTop: "10vh" },
      });
    } finally {
      setIsExecuting(false);
    }
  };

  const menuItems = modules.map((mod) => ({
    key: mod.id,
    label: mod.title,
    children: mod.lessons.map((lesson) => ({
      key: lesson.id,
      icon: lesson.iconName ? iconMap[lesson.iconName] : null,
      label: lesson.title,
    })),
  }));

  if (loading || !pyodide) {
    return (
      <ConfigProvider
        theme={{ token: { fontFamily: "'Plus Jakarta Sans', sans-serif" } }}
      >
        <div
          style={{
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            background: "transparent",
          }}
        >
          <Spin size="large" />
          <Title
            level={2}
            className="text-gradient"
            style={{ marginTop: 24, fontWeight: 900 }}
          >
            Igniting Environment...
          </Title>
          <Text
            type="secondary"
            style={{ fontSize: "18px", letterSpacing: "1px" }}
          >
            <Space>
              Loading WebAssembly Python Engine <CodeOutlined />
            </Space>
          </Text>
        </div>
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider
      theme={{
        token: {
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          colorPrimary: "#6366f1",
          colorSuccess: "#10b981",
          borderRadius: 20,
        },
        components: {
          Menu: {
            horizontalItemSelectedColor: "#6366f1",
            itemHoverColor: "#ec4899",
            activeBarHeight: 4,
          },
        },
      }}
    >
      <Layout className="site-layout" style={{ height: "100vh" }}>
        {/* FLOATING PILL HEADER */}
        <div style={{ padding: "24px 40px 0 40px" }}>
          <Header
            className="glass-pill premium-shadow"
            style={{
              display: "flex",
              alignItems: "center",
              padding: "0 32px",
              height: "80px",
              zIndex: 10,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginRight: "48px",
              }}
            >
              <div
                style={{
                  background: "linear-gradient(135deg, #4f46e5, #ec4899)",
                  borderRadius: "16px",
                  padding: "10px",
                  display: "flex",
                  marginRight: "16px",
                  boxShadow: "0 10px 25px rgba(236, 72, 153, 0.4)",
                }}
              >
                <ThunderboltFilled
                  style={{ fontSize: "22px", color: "#ffffff" }}
                />
              </div>
              <Title
                level={3}
                style={{
                  color: "#0f172a",
                  margin: 0,
                  fontWeight: 900,
                  letterSpacing: "-1px",
                  fontSize: "26px",
                }}
              >
                Py<span className="text-gradient">Course</span>
              </Title>
            </div>
            <Menu
              mode="horizontal"
              selectedKeys={[activeLessonId]}
              onClick={(e) => setActiveLessonId(e.key)}
              items={menuItems}
              style={{
                flex: 1,
                minWidth: 0,
                borderBottom: "none",
                fontSize: "14px",
                fontWeight: 700,
                background: "transparent",
              }}
            />
          </Header>
        </div>

        {/* MAIN WORKSPACE */}
        <Content
          style={{
            padding: "32px 40px",
            display: "flex",
            flexDirection: "column",
            height: "calc(100vh - 104px)",
          }}
        >
          <Row gutter={40} style={{ flexGrow: 1, height: "100%" }}>
            {/* LEFT PANEL: INSTRUCTIONS */}
            <Col span={24} xl={10} style={{ height: "100%" }}>
              <div
                className="premium-shadow"
                style={{
                  background: "rgba(255, 255, 255, 0.85)",
                  backdropFilter: "blur(40px)",
                  padding: "48px",
                  height: "100%",
                  overflowY: "auto",
                  borderRadius: "32px",
                  border: "1px solid rgba(255, 255, 255, 0.6)",
                  position: "relative",
                }}
              >
                {/* Decorative Background Blob */}
                <div
                  style={{
                    position: "absolute",
                    top: "-50px",
                    right: "-50px",
                    width: "200px",
                    height: "200px",
                    background:
                      "radial-gradient(circle, rgba(236,72,153,0.15) 0%, transparent 70%)",
                    borderRadius: "50%",
                    zIndex: 0,
                    pointerEvents: "none",
                  }}
                />

                <div style={{ position: "relative", zIndex: 1 }}>
                  <Space align="center" style={{ marginBottom: 16 }}>
                    <Tag
                      color={
                        activeLesson?.difficulty === "Beginner"
                          ? "cyan"
                          : activeLesson?.difficulty === "Intermediate"
                            ? "indigo"
                            : "magenta"
                      }
                      style={{
                        borderRadius: "24px",
                        padding: "6px 20px",
                        fontSize: "15px",
                        fontWeight: 800,
                        border: "none",
                      }}
                    >
                      {activeLesson?.difficulty}
                    </Tag>
                  </Space>

                  <Title
                    level={1}
                    style={{
                      margin: "0 0 32px 0",
                      color: "#0f172a",
                      fontWeight: 900,
                      fontSize: "2.5rem",
                      letterSpacing: "-1px",
                    }}
                  >
                    <Space align="center" size="middle">
                      <span style={{ color: "#ec4899" }}>
                        {activeLesson?.iconName
                          ? iconMap[activeLesson.iconName]
                          : null}
                      </span>
                      {activeLesson?.title}
                    </Space>
                  </Title>

                  <div
                    className="markdown-content"
                    style={{ color: "#334155" }}
                  >
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw]}
                    >
                      {activeLesson?.content || ""}
                    </ReactMarkdown>
                  </div>

                  {activeLesson?.type === "exercise" && (
                    <>
                      <div
                        style={{
                          marginTop: "48px",
                          padding: "32px",
                          background:
                            "linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)",
                          border: "1px solid #86efac",
                          borderRadius: "24px",
                          boxShadow: "0 10px 30px rgba(34, 197, 94, 0.1)",
                        }}
                      >
                        <Space style={{ marginBottom: 16 }}>
                          <CheckCircleFilled
                            style={{ color: "#10b981", fontSize: "28px" }}
                          />
                          <Text
                            strong
                            style={{
                              fontSize: "22px",
                              color: "#065f46",
                              fontWeight: 900,
                            }}
                          >
                            Your Mission
                          </Text>
                        </Space>
                        <div
                          style={{
                            margin: 0,
                            color: "#064e3b",
                            fontSize: "17px",
                            lineHeight: "1.7",
                          }}
                        >
                          Complete the code in the editor to the right and click{" "}
                          <strong>Run Code</strong> to evaluate your script.
                        </div>
                      </div>

                      {activeLesson.solution && (
                        <div style={{ marginTop: "32px" }}>
                          <Button
                            type="dashed"
                            icon={<BulbOutlined />}
                            onClick={() => setShowSolution(!showSolution)}
                            size="large"
                            style={{
                              borderRadius: "20px",
                              fontWeight: 800,
                              fontSize: "16px",
                              height: "56px",
                              color: "#4f46e5",
                              borderColor: "#a5b4fc",
                              background: showSolution
                                ? "#e0e7ff"
                                : "transparent",
                              width: "100%",
                            }}
                          >
                            {showSolution
                              ? "Hide Solution"
                              : "Stuck? Reveal the Magic"}
                          </Button>

                          {showSolution && (
                            <div
                              style={{
                                marginTop: "24px",
                                padding: "32px",
                                background: "#f8fafc",
                                borderRadius: "24px",
                                border: "2px dashed #cbd5e1",
                                animation:
                                  "fadeIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
                              }}
                            >
                              <Text
                                strong
                                style={{
                                  color: "#475569",
                                  marginBottom: "16px",
                                  display: "block",
                                  fontSize: "16px",
                                }}
                              >
                                Solution Key:
                              </Text>
                              <pre
                                className="code-editor"
                                style={{
                                  margin: 0,
                                  color: "#0f172a",
                                  whiteSpace: "pre-wrap",
                                  fontSize: "15px",
                                  background: "#e2e8f0",
                                  padding: "24px",
                                  borderRadius: "16px",
                                }}
                              >
                                {activeLesson.solution}
                              </pre>

                              <Button
                                type="primary"
                                size="large"
                                onClick={() => {
                                  setCode(activeLesson.solution || "");
                                  message.success(
                                    "Solution pasted! Now run it to see how it works.",
                                  );
                                }}
                                style={{
                                  marginTop: "24px",
                                  borderRadius: "16px",
                                  background: "#4f46e5",
                                  fontWeight: 700,
                                }}
                              >
                                Copy to Editor
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </Col>

            {/* RIGHT PANEL: EDITOR & CONSOLE */}
            <Col
              span={24}
              xl={14}
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "32px",
              }}
            >
              {/* Actual Code Editor Area */}
              <div
                className="premium-shadow"
                style={{
                  flex: 2,
                  minHeight: 0,
                  background:
                    "linear-gradient(180deg, #0f172a 0%, #020617 100%)",
                  borderRadius: "32px",
                  padding: "32px",
                  display: "flex",
                  flexDirection: "column",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "24px",
                  }}
                >
                  <Space size="large">
                    <div style={{ display: "flex", gap: "10px" }}>
                      <Badge
                        color="#ef4444"
                        style={{ transform: "scale(1.5)" }}
                      />
                      <Badge
                        color="#f59e0b"
                        style={{ transform: "scale(1.5)" }}
                      />
                      <Badge
                        color="#10b981"
                        style={{ transform: "scale(1.5)" }}
                      />
                    </div>
                    <Text
                      className="code-editor"
                      style={{
                        color: "#94a3b8",
                        fontSize: "16px",
                        letterSpacing: "1px",
                        fontWeight: 700,
                      }}
                    >
                      script.py
                    </Text>
                  </Space>
                  {activeLesson?.type === "exercise" && (
                    <Button
                      type="primary"
                      className="run-button"
                      icon={!isExecuting ? <PlayCircleFilled /> : undefined}
                      loading={isExecuting}
                      onClick={handleRunCode}
                      size="large"
                      style={{
                        fontWeight: 900,
                        borderRadius: "20px",
                        padding: "0 32px",
                        height: "48px",
                        fontSize: "17px",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {isExecuting ? "Executing..." : "RUN CODE"}
                    </Button>
                  )}
                </div>

                {activeLesson?.type === "exercise" ? (
                  <textarea
                    className="code-editor"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    style={{
                      width: "100%",
                      flex: 1,
                      background: "transparent",
                      color: "#f8fafc",
                      border: "none",
                      fontSize: "18px",
                      resize: "none",
                      outline: "none",
                      lineHeight: "1.8",
                    }}
                    spellCheck="false"
                  />
                ) : (
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px dashed #334155",
                      borderRadius: "20px",
                      background: "rgba(255,255,255,0.02)",
                    }}
                  >
                    <Text
                      style={{
                        color: "#64748b",
                        fontSize: "20px",
                        fontWeight: 600,
                      }}
                    >
                      <Space>
                        Sit back and relax! No coding required for this concept
                        lesson. <CoffeeOutlined />
                      </Space>
                    </Text>
                  </div>
                )}
              </div>

              {/* Real Console Terminal Area */}
              <div
                className="premium-shadow"
                style={{
                  flex: 1,
                  minHeight: 0,
                  background: "#020617",
                  borderRadius: "32px",
                  padding: "32px",
                  overflowY: "auto",
                  border: "1px solid rgba(255,255,255,0.05)",
                  position: "relative",
                }}
              >
                <Text
                  style={{
                    color: "#475569",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontWeight: 900,
                    display: "block",
                    marginBottom: 20,
                    fontSize: "14px",
                    textTransform: "uppercase",
                    letterSpacing: "2px",
                  }}
                >
                  Terminal Output
                </Text>
                <pre
                  className={`code-editor ${isError ? "terminal-glow-error" : "terminal-glow"}`}
                  style={{
                    color: isError ? "#f87171" : "#34d399",
                    margin: 0,
                    fontSize: "17px",
                    whiteSpace: "pre-wrap",
                    lineHeight: "1.7",
                  }}
                >
                  {output || "Waiting for execution..."}
                </pre>
              </div>
            </Col>
          </Row>
        </Content>
      </Layout>
    </ConfigProvider>
  );
};

export default App;
